import React, { useState } from "react";
import { useTranslation } from "react-i18next"; // i18n
import useBlockchainGraph from "../hooks/useBlockchainGraph"; // 自訂 Hook
import API_URL from "../config";
import * as d3 from "d3";

// 工具函式 (下載SVG、下載JSON、複製連結)
function saveSvg(svgEl, name) {
  svgEl.setAttribute("xmlns", "http://www.w3.org/2000/svg");
  const svgData = svgEl.outerHTML;
  const preface = '<?xml version="1.0" standalone="no"?>\r\n';
  const svgBlob = new Blob([preface, svgData], {
    type: "image/svg+xml;charset=utf-8",
  });
  const downloadLink = document.createElement("a");
  downloadLink.href = URL.createObjectURL(svgBlob);
  downloadLink.download = name;
  document.body.appendChild(downloadLink);
  downloadLink.click();
  document.body.removeChild(downloadLink);
}

function saveText(text, name) {
  const file = new Blob([text], { type: "text/plain;charset=utf-8" });
  const downloadLink = document.createElement("a");
  downloadLink.href = URL.createObjectURL(file);
  downloadLink.download = name;
  document.body.appendChild(downloadLink);
  downloadLink.click();
  document.body.removeChild(downloadLink);
}

function copyToClipboard(text) {
  const textarea = document.createElement("textarea");
  textarea.value = text;
  textarea.setAttribute("readonly", "");
  textarea.style.position = "absolute";
  textarea.style.left = "-9999px";
  document.body.appendChild(textarea);
  textarea.select();
  document.execCommand("copy");
  document.body.removeChild(textarea);
}

const BlockchainVisualization = () => {
  const { t } = useTranslation();
  const [filterAddress, setFilterAddress] = useState(""); // 篩選地址
  const [hoveredNode, setHoveredNode] = useState(null); // 管理高亮的節點
  const {
    svgRef,
    selectedChainType,
    setSelectedChainType,
    dateMin,
    setDateMin,
    dateMax,
    setDateMax,
    currentAddress,
    allTransactions = [], // 確保初始為空陣列
    trace,
    fetchBalance,
    balanceData,
    isFetchingBalance,
  } = useBlockchainGraph(setHoveredNode);

  // 過濾交易：檢查是否包含篩選地址
  const filteredTransactions = allTransactions.filter((tx) => {
    const sourceMatch = tx.source.some((src) =>
      src.address.toLowerCase().includes(filterAddress.toLowerCase())
    );
    const targetMatch = tx.target.some((tgt) =>
      tgt.address.toLowerCase().includes(filterAddress.toLowerCase())
    );
    return sourceMatch || targetMatch;
  });
  // 新增格式化工具函數
  const formatNumberAbbr = (num) => {
    const n = parseFloat(num);
    if (isNaN(n)) return "-";
    if (Math.abs(n) >= 1e12) {
      return (n / 1e12).toFixed(2) + "T";
    } else if (Math.abs(n) >= 1e9) {
      return (n / 1e9).toFixed(2) + "B";
    } else if (Math.abs(n) >= 1e6) {
      return (n / 1e6).toFixed(2) + "M";
    } else if (Math.abs(n) >= 1e3) {
      return (n / 1e3).toFixed(2) + "K";
    }
    return n.toFixed(2);
  };
  return (
    <div className="w-full min-h-screen dark:bg-gray-900 bg-gray-100 text-black dark:text-white p-4">
      <h1 className="text-xl font-bold mb-4">{t("blockchain.visTitle")}</h1>

      {/* 選擇區塊 */}
      <div className="flex flex-col md:flex-row gap-4 mb-4">
        <div>
          <label className="block mb-1">{t("blockchain.selectChainType")}</label>
          <select
            className="border p-2 rounded"
            value={selectedChainType}
            onChange={(e) => setSelectedChainType(e.target.value)}
          >
            <option value="BTC">Bitcoin</option>
            <option value="ETH">ETH</option>
            <option value="TRON">TRON</option>
            <option value="BSC">BSC</option>
          </select>
        </div>
        <div>
          <label className="block mb-1">{t("blockchain.dateFrom")}</label>
          <input
            type="date"
            className="border p-2 rounded"
            defaultValue="2024-01-01"
            onChange={(e) => setDateMin(new Date(e.target.value).getTime() / 1000)}
          />
        </div>
        <div>
          <label className="block mb-1">{t("blockchain.dateTo")}</label>
          <input
            type="date"
            className="border p-2 rounded"
            defaultValue={new Date().toISOString().split("T")[0]}
            onChange={(e) => setDateMax(new Date(e.target.value).getTime() / 1000)}
          />
        </div>
      </div>

      {/* 查詢區塊 */}
      <div className="flex flex-col md:flex-row items-center gap-2 mb-4">
        <input
          type="text"
          id="hash"
          placeholder={t("blockchain.addressOrTx")}
          className="border p-2 rounded flex-1"
        />
        <button
          className="bg-blue-600 text-white px-8 py-2 rounded"
          onClick={() => {
            const hashInput = document.getElementById("hash");
            if (hashInput) trace(hashInput.value);
          }}
        >
          {t("追蹤")}
        </button>
      </div>

      {/* 畫布與交易列表 */}
      <div className="flex flex-col md:flex-row gap-4">
        {/* 畫布 */}
        <div
          className="border border-gray-500 rounded-lg bg-white dark:bg-gray-700 relative"
          style={{ width: "1100px", height: "900px" }}
        >
          <svg
            ref={svgRef}
            className="w-full h-full"
            style={{ display: "block", cursor: "grab" }}
            onMouseLeave={() => setHoveredNode(null)} // 清除高亮節點
          ></svg>
        </div>

        {/* 交易列表 */}
        <div className="w-full md:w-1/2 p-2 border border-gray-300 rounded">
          <h2 className="font-semibold mb-2">
            {t("查詢地址：")} {currentAddress}
            <button
              className="ml-2 bg-gray-200 text-gray-600 px-2 py-1 rounded text-sm"
              onClick={() => {
                navigator.clipboard.writeText(currentAddress).catch((err) => {
                  console.error("複製失敗:", err);
                });
              }}
            >
              {t("複製")}
            </button>
            {/* 顯示 Address 的 Label */}
            <p>
              <strong>Label:</strong>{" "}
              {
                // 從流入的 `from` 資料中找到對應的 `label`
                filteredTransactions
                  .flatMap((tx) => tx.source)
                  .find((src) => src.address.toLowerCase() === currentAddress.toLowerCase())?.label || t("未知")
              }
            </p>
            {/* 顯示 Address 的 代幣餘額 */}
            <div>
              {isFetchingBalance ? (
                <p>正在查詢餘額...</p>
              ) : selectedChainType.toUpperCase() === "BTC" && balanceData && balanceData.length > 0 ? (
                <div>
                  <p>BTC 餘額：{balanceData[0].balance} BTC</p>
                </div>
              ) : balanceData && balanceData.length > 0 ? (
                (() => {
                  const allTokens = balanceData.flatMap(
                    (pageData) => pageData.tokenList || []
                  );
                  const sortedTokens = [...allTokens].sort(
                    (a, b) => parseFloat(b.valueUsd || 0) - parseFloat(a.valueUsd || 0)
                  );
                  return (
                    <div style={{ maxHeight: "320px", overflowY: "auto" }}>
                      <table className="min-w-full border-collapse border border-gray-300">
                        <thead className="sticky top-0 bg-white">
                          <tr>
                            <th className="px-4 py-2 border">代幣名稱</th>
                            <th className="px-4 py-2 border">持有量</th>
                            <th className="px-4 py-2 border">總價值</th>
                          </tr>
                        </thead>
                        <tbody>
                          {sortedTokens.map((token, idx) => (
                            <tr key={idx}>
                              <td className="px-4 py-2 border">{token.token || "-"}</td>
                              <td className="px-4 py-2 border">
                                {token.holdingAmount ? formatNumberAbbr(token.holdingAmount) : "-"}
                              </td>
                              <td className="px-4 py-2 border">
                                {token.valueUsd ? formatNumberAbbr(token.valueUsd) : "-"} USDT
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  );
                })()
              ) : (
                <p>無法獲取餘額或無數據。</p>
              )}
            </div>
          </h2>

          {/* 篩選器區塊 */}
          <div className="flex flex-col md:flex-row items-center gap-2 mb-4">
            <input
              type="text"
              placeholder={t("篩選地址")}
              className="border p-2 rounded flex-1"
              value={filterAddress}
              onChange={(e) => setFilterAddress(e.target.value.trim())}
            />
          </div>

          <div className="overflow-y-auto" style={{ maxHeight: "800px" }}>
            <div className="mb-4">
              <h3 className="font-bold">流入 (Inflow)</h3>
              <ul className="list-disc ml-5">
                {filteredTransactions.map((tx, index) =>
                  (tx.source || [])
                    .filter((src) => src.address.toLowerCase() !== currentAddress.toLowerCase())
                    .map((src, idx) => (
                      <li
                        key={`in-${index}-${idx}`}
                        onMouseEnter={() => setHoveredNode(src.address)}
                        onMouseLeave={() => setHoveredNode(null)}
                        className={`inflow-transaction ${hoveredNode === src.address ? "bg-yellow-200" : ""}`}
                      >
                        <div><strong>From:</strong> {src.address}</div>
                        <div><strong>Amount:</strong> {tx.value}</div>
                        <div><strong>TxID:</strong> {tx.hash}</div>
                        <div><strong>Label:</strong> {src.label}</div>
                      </li>
                    ))
                )}
              </ul>
            </div>
            <div>
              <h3 className="font-bold">流出 (Outflow)</h3>
              <ul className="list-disc ml-5">
                {filteredTransactions.map((tx, index) =>
                  tx.target
                    .filter((tgt) => tgt.address.toLowerCase() !== currentAddress.toLowerCase())
                    .map((tgt, idx) => (
                      <li
                        key={`out-${index}-${idx}`}
                        onMouseEnter={() => setHoveredNode(tgt.address)}
                        onMouseLeave={() => setHoveredNode(null)}
                        className={`outflow-transaction ${hoveredNode === tgt.address ? "bg-yellow-200" : ""}`}
                      >
                        <div><strong>To:</strong> {tgt.address}</div>
                        <div><strong>Amount:</strong> {tx.value}</div>
                        <div><strong>TxID:</strong> {tx.hash}</div>
                        <div><strong>Label:</strong> {tgt.label}</div>
                      </li>
                    ))
                )}
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BlockchainVisualization;